import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { ListNode, Tree } from "components/objectTree/types";
import { ensureExpandedTo, getParentPaths, makeNodeList } from "components/objectTree/utils";
import { EmptyState } from "components/window/emptyState";
import { SlottedWindow } from "components/window/SlottedWindow";
import { useWindowPlatformMode } from "components/window/useWindowPlatformMode";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { readyPdfObjectIds } from "contexts/explorerGlobals/reducer";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useSetObjectTree } from "contexts/objectTree";
import { type BaseWindowProps } from "contexts/windows";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { ModelTree } from "features/modelTree/components/modelTree";
import { emptyFileTypeFilters } from "features/modelTree/constants";
import { ModelTreeNodeData } from "features/modelTree/types";
import { expandNode, loadMore, selectNode } from "features/modelTree/utils";
import { capturePropertyFilter } from "features/properties/slice";
import {
    renderActions,
    selectDefaultVisibility,
    selectMainObject,
    selectMainObjects,
    selectPreIsolationVisibility,
} from "features/render";
import { ObjectVisibility } from "features/render/types";
import { useAbortController } from "hooks/useAbortController";
import { useDbStatus } from "hooks/useDbStatus";
import { MAX_SEARCH_RESULTS, useObjectTreeSearch } from "hooks/useObjectTreeSearch";
import NoSearchResultsMobileIcon from "media/icons/noSearchResultsMobile.svg?react";
import { selectIsProcoreDesign } from "slices/explorer";
import { telemetry } from "telemetry";
import {
    bulkSelectAllHierarchies,
    captureVisibilitySnapshot,
    hideObjectHierarchy,
    hideObjectHierarchyFromFilteredTree,
    isolateObjects,
    isolateObjectsInXRayMode,
    selectObjectHierarchy,
    showObjectHierarchy,
    showObjectHierarchyFromFilteredTree,
    unisolateObjects,
    unisolateObjectsFromXRayMode,
    unselectObjectHierarchy,
} from "utils/objectActions";
import { SYNTHETIC_NODE_ID } from "utils/objectPath";

import { ContextMenuPopup } from "../hooks/ContextMenuPopup";
import { MobileObjectTreeLevel } from "./components/MobileObjectTreeLevel";
import { MobileSearchResults } from "./components/MobileSearchResults";
import { ObjectTreeNodeProcoreV2 } from "./components/ObjectTreeNodeProcoreV2";
import { useIsolationVisibility } from "./hooks/useIsolationVisibility";
import { SelectionContext } from "./selectionContext";
import { BulkAction } from "./utils/bulkActions";
import { nextBulkExpandMark } from "./utils/bulkExpandGate";
import { formatNodeDisplayName } from "./utils/formatting";
import { isSelectionEmpty, pushSelectionUndo, pushVisibilityUndo, type SelectionSnapshot } from "./utils/undoEntries";

const ITEM_HEIGHT_V2 = 30;

const enum SelectionMode {
    Range = "range",
    Toggle = "toggle",
    Single = "single",
}

function getSelectionMode(event: React.MouseEvent, hasAnchor: boolean): SelectionMode {
    if (event.shiftKey && hasAnchor) {
        return SelectionMode.Range;
    }
    if (event.ctrlKey || event.metaKey) {
        return SelectionMode.Toggle;
    }
    return SelectionMode.Single;
}

type ListItem = ReturnType<typeof makeNodeList>[number];

// Walk `tree` and collect paths whose node id is in `ids`. When `primaryId` is
// provided, also record the path of the node with that id. Shared by the three
// viewer-pick sync effects, which otherwise duplicated this loop.
function collectPathsForIds(
    tree: Tree<ModelTreeNodeData>,
    ids: Iterable<number>,
    primaryId?: number | null,
): { paths: Set<string>; primaryPath: string | null } {
    const idSet = ids instanceof Set ? ids : new Set(ids);
    const paths = new Set<string>();
    let primaryPath: string | null = null;
    for (const node of Object.values(tree)) {
        if (node.path === "") {
            continue;
        }
        const nodeId = node.data.object.id;
        if (nodeId !== SYNTHETIC_NODE_ID && idSet.has(nodeId)) {
            paths.add(node.path);
        }
        if (primaryId != null && nodeId === primaryId) {
            primaryPath = node.path;
        }
    }
    return { paths, primaryPath };
}

/**
 * Row paths for a viewer pick, plus ancestor expand for ids already in the tree.
 * Ensures that selected rows are actually visible by expanding collapsed ancestors.
 */
function applyViewerPickToLoadedTree(
    tree: Tree<ModelTreeNodeData>,
    ids: Iterable<number>,
    primaryId?: number | null,
): { tree: Tree<ModelTreeNodeData>; paths: Set<string>; primaryPath: string | null } {
    const { paths, primaryPath } = collectPathsForIds(tree, ids, primaryId);
    if (primaryPath) {
        if (!paths.has(primaryPath)) {
            paths.clear();
        }
        paths.add(primaryPath);
    }
    let next = tree;
    for (const path of paths) {
        next = ensureExpandedTo(next, path);
    }
    return { tree: next, paths, primaryPath };
}

function computeNewSelectedPaths(
    mode: SelectionMode,
    path: string,
    list: ListItem[],
    selectedRowPaths: Set<string>,
    anchorRowPath: string | null,
): { paths: Set<string>; nextAnchor: string | null } {
    switch (mode) {
        case SelectionMode.Range: {
            const anchorIdx = list.findIndex((i) => i.node.path === anchorRowPath);
            const clickedIdx = list.findIndex((i) => i.node.path === path);
            if (anchorIdx !== -1 && clickedIdx !== -1) {
                const [from, to] = anchorIdx < clickedIdx ? [anchorIdx, clickedIdx] : [clickedIdx, anchorIdx];
                return { paths: new Set(list.slice(from, to + 1).map((i) => i.node.path)), nextAnchor: anchorRowPath };
            }
            return { paths: new Set([path]), nextAnchor: anchorRowPath };
        }
        case SelectionMode.Toggle: {
            // Intentional toggle: Cmd/Ctrl+clicking an already-selected row deselects it.
            // BM-12619 inadvertently reverts this to always-add — keep this behavior on rebase.
            const paths = new Set(selectedRowPaths);
            if (paths.has(path)) {
                paths.delete(path);
                return { paths, nextAnchor: anchorRowPath };
            }
            paths.add(path);
            return { paths, nextAnchor: path };
        }
        case SelectionMode.Single: {
            const isOnlySelected = selectedRowPaths.size === 1 && selectedRowPaths.has(path);
            return {
                paths: isOnlySelected ? new Set() : new Set([path]),
                nextAnchor: isOnlySelected ? null : path,
            };
        }
    }
}

export function ObjectTreeWindowV2({ onClose, position }: BaseWindowProps) {
    const { t } = useTranslation();
    const {
        state: { dataAccess, pdfObjectDiscovery },
    } = useExplorerGlobals(true);

    const {
        searchTerm,
        filteredObjectIds,
        tree,
        setTree,
        treeKey,
        isSearching,
        hasNoResults,
        searchError,
        isFilterActive,
        resultsTruncated,
        handleSearchChange,
        getBaseTree,
    } = useObjectTreeSearch(dataAccess);

    const setTreeContext = useSetObjectTree();
    useEffect(() => {
        setTreeContext(tree);
    }, [tree, setTreeContext]);

    const dispatch = useAppDispatch();
    const store = useAppStore();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const hidden = useHidden();
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;
    const mainObject = useAppSelector(selectMainObject);
    const mainObjects = useAppSelector(selectMainObjects);
    const dbStatus = useDbStatus();
    const { ids: highlightedIds } = useHighlighted();
    const [abortController] = useAbortController();
    // Dedicated controller for selection hierarchy fetches. Each new selection action
    // aborts and replaces it so a superseded run (e.g. Cmd+A still resolving descendants
    // when the user clicks a single row) cannot dispatch stale highlights afterwards.
    // Kept separate from `abortController` so a row click does not cancel a bulk hide/show.
    const [selectAbortController, abortSelection] = useAbortController();
    const [drillAbortController, abortDrill] = useAbortController();
    const [loadMoreAbortController] = useAbortController();
    const [loading, setLoading] = useState(false);
    const { flyToSelected } = useFlyToSelected();

    const { isMobile, isDrawerOpen, isSidePanelOpen } = useWindowPlatformMode();
    const skipPdfFiles = useAppSelector(selectIsProcoreDesign);
    const knownPdfObjectIds = readyPdfObjectIds(pdfObjectDiscovery);
    // Navigation stack for mobile drill-down: each entry is the path of the node drilled into.
    // Empty = showing root tier.
    const [stack, setStack] = useState<string[]>([]);
    const currentPath = useMemo(() => stack.at(-1) ?? "", [stack]);

    const [selectedRowPaths, setSelectedRowPaths] = useState<Set<string>>(new Set());
    const anchorRowPathRef = useRef<string | null>(null);
    // Set to true when the selection was initiated from within the tree so the
    // mainObject effect doesn't clear selectedRowPaths on the resulting Redux update.
    const treeInitiatedRef = useRef(false);
    // ID of a viewer-originated pick waiting for ModelTree to load its node into the tree.
    const pendingViewerSelectRef = useRef<number | null>(null);
    // Incremented on each row selection; lets concurrent async invocations detect they've been superseded.
    const selectGenerationRef = useRef(0);
    // Same pattern for search-result group taps so a failed/aborted load cannot clobber a newer one.
    const selectSearchGroupGenerationRef = useRef(0);
    // Read-only ref so effects can read the current tree without listing it as a dep.
    const treeRef = useRef(tree);
    treeRef.current = tree;
    const setTreeRef = useRef(setTree);
    setTreeRef.current = setTree;
    // Path currently being paged on mobile; guards against a second IO callback
    // firing before node.loading is reflected in treeRef.
    const mobileLoadingPath = useRef<string | null>(null);
    // Read-only ref so the selection handlers can snapshot the highlight set for undo
    // without listing it as a dependency (a viewer pick would otherwise rebuild them).
    const highlightedIdsRef = useRef(highlightedIds);
    highlightedIdsRef.current = highlightedIds;

    // Read-only ref so Effects 1 and 3 always see the current mainObjects without
    // listing them as a dep on the mainObject effect (avoids double-firing).
    const mainObjectsRef = useRef(mainObjects);
    mainObjectsRef.current = mainObjects;
    const mainObjectRef = useRef(mainObject);
    mainObjectRef.current = mainObject;
    // Selection as it is right now, for the `before` side of an undo entry.
    const captureSelection = useCallback(
        (): SelectionSnapshot => ({
            highlightedIds: highlightedIdsRef.current,
            mainObject: mainObjectRef.current,
            mainObjects: mainObjectsRef.current,
        }),
        [],
    );
    const hiddenIdsRef = useRef(hidden.ids);
    hiddenIdsRef.current = hidden.ids;
    // True while the active selection originated from a viewer pick (not a tree row click).
    // Lets the highlightedIds effect re-sync tree rows when highlighted updates in a
    // separate render from mainObject (React 18 doesn't always batch cross-store dispatches).
    const isViewerPickRef = useRef(false);
    const viewerPickMainObjectRef = useRef<number | null>(null);
    // Incremented on each new viewer pick. Ancestors load asynchronously, so a pick's rows
    // resolve in batches; lastBulkExpandRef records the pick and how many rows were resolved
    // the last time ancestors were expanded. Expanding only when that count grows lets each
    // batch expand once while leaving a user's own collapse alone (collapsing doesn't change
    // how many selected ids resolve to paths).
    const bulkPickVersionRef = useRef(0);
    const lastBulkExpandRef = useRef({ version: 0, pathCount: 0 });

    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);

    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

    // Keep this in lockstep with ModelTree's grouping={false}: this list drives
    // shift-click/range selection while ModelTree independently flattens the rendered rows.
    const list = useMemo(() => makeNodeList({ tree, grouping: false }), [tree]);

    // True when every selected row is hidden — drives the Hide/Unhide menu label.
    // Wrapped in useMemo: after Cmd+A the set can have thousands of paths, and the
    // scan would run on EVERY render (including the multiple re-renders triggered by
    // the Redux dispatches inside setHierarchyVisibleBulk). Memoizing limits the work
    // to runs where the selection, tree, or visibility actually changes.
    //
    // IMPORTANT: Use `for...of` instead of `[...selectedRowPaths].every(...)`.
    // The spread form allocates a full N-element Array before any iteration begins —
    // even when `every()` would short-circuit on the first item (e.g. after unhide-all
    // when `hidden.ids` just went from N → 0: the first id is no longer hidden, so
    // `every()` returns false immediately, but the spread already paid O(n) to create
    // the array). With `for...of` the iterator yields one item at a time, so a failing
    // first check exits in O(1) with zero intermediate allocation.
    const allSelectedHidden = useMemo(() => {
        if (selectedRowPaths.size === 0) {
            return false;
        }
        for (const p of selectedRowPaths) {
            const id = tree[p]?.data.object.id;
            if (id === undefined) {
                return false;
            }
            // In isolation mode: visible means in visibleIds → NOT hidden.
            // In normal mode: hidden means in hidden.ids.
            if (isolationVisibleIds ? isolationVisibleIds.has(id) : !hidden.ids.has(id)) {
                return false;
            }
        }
        return true;
    }, [selectedRowPaths, tree, isolationVisibleIds, hidden.ids]);

    // True when the scene is in plain (transparent) isolate mode.
    const isIsolated =
        selectedRowPaths.size > 0 &&
        !allSelectedHidden &&
        defaultVisibility === ObjectVisibility.Transparent &&
        preIsolationVisibility !== undefined;

    // True when the scene is already in X-Ray isolate mode triggered by a prior isolate action.
    const isIsolatedInXRay =
        selectedRowPaths.size > 0 &&
        !allSelectedHidden &&
        defaultVisibility === ObjectVisibility.Xray &&
        preIsolationVisibility !== undefined;

    // Viewer-originated pick: sync mainObjects (semantic selection) to tree row selection.
    // Uses mainObjects (the resolved first-object set) instead of highlighted (which also
    // includes descendant geometry) so the tree shows exactly what the user picked.
    useEffect(() => {
        if (treeInitiatedRef.current) {
            isViewerPickRef.current = false;
            return;
        }

        anchorRowPathRef.current = null;
        pendingViewerSelectRef.current = null;

        const currentMainObjects = mainObjectsRef.current;

        if (mainObject == null) {
            if (isViewerPickRef.current && currentMainObjects.length > 0) {
                // Ctrl+click deselected the primary object but others remain selected.
                const { paths: remainingPaths } = collectPathsForIds(treeRef.current, currentMainObjects);
                setSelectedRowPaths(remainingPaths);
                viewerPickMainObjectRef.current = null;
                return;
            }
            isViewerPickRef.current = false;
            viewerPickMainObjectRef.current = null;
            setSelectedRowPaths(new Set());
            return;
        }

        isViewerPickRef.current = true;
        viewerPickMainObjectRef.current = mainObject;
        bulkPickVersionRef.current++;

        const applied = applyViewerPickToLoadedTree(treeRef.current, currentMainObjects, mainObject);
        if (applied.paths.size > 0) {
            lastBulkExpandRef.current = { version: bulkPickVersionRef.current, pathCount: applied.paths.size };
            if (applied.tree !== treeRef.current) {
                setTreeRef.current(applied.tree);
            }
        }

        setSelectedRowPaths(applied.paths);
        if (applied.primaryPath) {
            anchorRowPathRef.current = applied.primaryPath;
        } else {
            pendingViewerSelectRef.current = mainObject;
        }
    }, [mainObject]);

    // Fallback: once ModelTree expands the ancestor chain and loads the pending node,
    // finalize the row selection. Also re-scans on every tree update while a bulk
    // viewer selection is active so rows become highlighted as useLoadAncestorsForSelection
    // resolves object paths and adds ancestor nodes asynchronously.
    useEffect(() => {
        if (pendingViewerSelectRef.current == null) {
            // Multi-pick: re-scan as the tree loads ancestor chains for box/lasso selections.
            if (isViewerPickRef.current && mainObjectsRef.current.length > 1) {
                const applied = applyViewerPickToLoadedTree(
                    treeRef.current,
                    mainObjectsRef.current,
                    viewerPickMainObjectRef.current,
                );

                if (applied.paths.size > 0) {
                    const nextMark = nextBulkExpandMark(
                        lastBulkExpandRef.current,
                        bulkPickVersionRef.current,
                        applied.paths.size,
                    );
                    if (nextMark) {
                        lastBulkExpandRef.current = nextMark;
                        if (applied.tree !== treeRef.current) {
                            setTreeRef.current(applied.tree);
                        }
                    }
                    setSelectedRowPaths(applied.paths);
                    if (applied.primaryPath) {
                        anchorRowPathRef.current = applied.primaryPath;
                    }
                }
            }
            return;
        }
        const targetId = pendingViewerSelectRef.current;
        const targetNode = Object.values(tree).find((n) => n.data.object.id === targetId);
        if (targetNode) {
            pendingViewerSelectRef.current = null;
            const applied = applyViewerPickToLoadedTree(treeRef.current, mainObjectsRef.current, targetId);
            if (applied.tree !== treeRef.current) {
                setTreeRef.current(applied.tree);
            }
            setSelectedRowPaths(applied.paths);
            anchorRowPathRef.current = targetNode.path;
        }
    }, [tree]);

    // Re-sync when mainObjects changes independently of mainObject: Ctrl+click deselect,
    // additive box selection, and Properties-window filters can all keep the same primary.
    // Tree writers update mainObjects last, so this is the final effect that consumes
    // treeInitiatedRef. The [mainObject] effect also reads the flag but must leave it set
    // or this effect would overwrite the tree's local row set and shift-click anchor.
    useEffect(() => {
        if (treeInitiatedRef.current) {
            treeInitiatedRef.current = false;
            return;
        }
        if (mainObjects.length === 0) {
            isViewerPickRef.current = false;
            viewerPickMainObjectRef.current = null;
            setSelectedRowPaths(new Set());
            return;
        }
        if (!isViewerPickRef.current) {
            // mainObject did not change, so the effect above could not claim this external
            // selection. Claim it here so later ancestor loads keep adding its rows.
            isViewerPickRef.current = true;
            viewerPickMainObjectRef.current = mainObjectRef.current ?? null;
            bulkPickVersionRef.current++;
        }
        const pickMainObject = viewerPickMainObjectRef.current;
        const applied = applyViewerPickToLoadedTree(treeRef.current, mainObjects, pickMainObject);
        const nextMark = nextBulkExpandMark(lastBulkExpandRef.current, bulkPickVersionRef.current, applied.paths.size);
        if (nextMark) {
            lastBulkExpandRef.current = nextMark;
            if (applied.tree !== treeRef.current) {
                setTreeRef.current(applied.tree);
            }
        }
        setSelectedRowPaths(applied.paths);
        if (applied.primaryPath && applied.paths.has(applied.primaryPath)) {
            anchorRowPathRef.current = applied.primaryPath;
        }
    }, [mainObjects]);

    // Mobile: reset stack to root when the panel/drawer closes (iOS nav stack convention).
    useEffect(() => {
        if (!isMobile) {
            return;
        }
        if (!isDrawerOpen && !isSidePanelOpen) {
            setStack([]);
        }
    }, [isMobile, isDrawerOpen, isSidePanelOpen]);

    // Mobile: expand the current tier's children if they haven't been loaded yet.
    useEffect(() => {
        if (!isMobile) {
            return;
        }
        abortDrill();
        const node = treeRef.current[currentPath];
        if (node?.children) {
            return;
        }

        const expander = expandNode({
            tree: treeRef.current,
            path: currentPath,
            dataAccess,
            abortSignal: drillAbortController.current.signal,
            skipPdfFiles,
            knownPdfObjectIds,
        });
        setTree(expander.tree);
        if (!expander.load) {
            return;
        }

        expander
            .load()
            .then((patch) => {
                setTree((t) => patch(t, true));
            })
            .catch((ex: unknown) => {
                if ((ex as { name?: string }).name !== "AbortError") {
                    telemetry.trackCaughtException({
                        source: "objectTreeWindowV2.drillExpand",
                        category: "data",
                        error: ex,
                        severity: "error",
                    });
                }
            });
    }, [isMobile, currentPath, dataAccess, skipPdfFiles, knownPdfObjectIds, abortDrill, drillAbortController, setTree]);

    const handleRowSelect = useCallback(
        async (path: string, event: React.MouseEvent) => {
            if (isIsolatedInXRay) {
                return;
            }
            const node = tree[path];
            if (!node) {
                return;
            }
            const generation = ++selectGenerationRef.current;
            // Cancel any in-flight selection fetch and capture the fresh signal once.
            // Re-reading `.current.signal` after an await would pick up a newer run's
            // controller and let this run keep dispatching after it was superseded.
            abortSelection();
            const signal = selectAbortController.current.signal;
            pendingViewerSelectRef.current = null;
            isViewerPickRef.current = false;
            viewerPickMainObjectRef.current = null;
            // Before any dispatch: the undo side of the entry pushed once the write lands.
            const before = captureSelection();
            const beforeHighlighted = highlightedIdsRef.current;
            const recordSelection = (after: SelectionSnapshot) =>
                pushSelectionUndo({
                    dispatch,
                    dispatchHighlighted,
                    description: isSelectionEmpty(after) ? "Unselect objects" : "Select objects",
                    before,
                    after,
                });

            const mode = getSelectionMode(event, anchorRowPathRef.current !== null);
            const { paths: newPaths, nextAnchor } = computeNewSelectedPaths(
                mode,
                path,
                list,
                selectedRowPaths,
                anchorRowPathRef.current,
            );
            anchorRowPathRef.current = nextAnchor;
            setSelectedRowPaths(newPaths);

            // Toggle mode (cmd/ctrl+click): add or remove just the clicked node's hierarchy
            // additively — never clear all highlights — so selections built across multiple
            // different searches accumulate in the viewer instead of being wiped.
            if (mode === SelectionMode.Toggle) {
                const isAdding = newPaths.has(path);
                let affectedIds: number[] = [];
                try {
                    if (isAdding) {
                        affectedIds = await selectObjectHierarchy({
                            nodes: [node.data.object],
                            dataAccess,
                            abortSignal: signal,
                            setLoading,
                            dispatchHighlighted,
                        });
                    } else {
                        affectedIds = await unselectObjectHierarchy({
                            nodes: [node.data.object],
                            dataAccess,
                            abortSignal: signal,
                            setLoading,
                            dispatchHighlighted,
                        });
                    }
                } catch (error) {
                    if (!signal.aborted) {
                        telemetry.trackCaughtException({
                            source: "objectTreeWindowV2.handleRowSelect",
                            category: "data",
                            error,
                            severity: "error",
                        });
                    }
                }
                if (signal.aborted || selectGenerationRef.current !== generation) {
                    return;
                }
                if (isAdding) {
                    treeInitiatedRef.current = true;
                    dispatch(renderActions.setMainObject(node.data.object.id));
                    if (node.data.object.id !== SYNTHETIC_NODE_ID) {
                        dispatch(renderActions.addToMainObjects([node.data.object.id]));
                    }
                    recordSelection({
                        highlightedIds: beforeHighlighted.withAdded(affectedIds),
                        mainObject: node.data.object.id,
                        mainObjects:
                            node.data.object.id !== SYNTHETIC_NODE_ID && !mainObjects.includes(node.data.object.id)
                                ? [...mainObjects, node.data.object.id]
                                : mainObjects,
                    });
                } else if (newPaths.size === 0) {
                    treeInitiatedRef.current = true;
                    dispatch(renderActions.setMainObject(undefined));
                    dispatch(renderActions.clearMainObjects());
                    recordSelection({
                        highlightedIds: beforeHighlighted.withRemoved(affectedIds),
                        mainObject: undefined,
                        mainObjects: [],
                    });
                } else {
                    treeInitiatedRef.current = true;
                    dispatch(renderActions.removeFromMainObjects([node.data.object.id]));
                    // If the deselected row was the primary, promote another remaining id
                    // so mainObject stays a member of mainObjects.
                    if (node.data.object.id === mainObject) {
                        const remaining = mainObjects.find((id) => id !== node.data.object.id);
                        dispatch(renderActions.setMainObject(remaining));
                    }
                    recordSelection({
                        highlightedIds: beforeHighlighted.withRemoved(affectedIds),
                        mainObject:
                            node.data.object.id === mainObject
                                ? mainObjects.find((id) => id !== node.data.object.id)
                                : mainObject,
                        mainObjects: mainObjects.filter((id) => id !== node.data.object.id),
                    });
                }
                return;
            }

            // Single / Range: resolve every selected node's ids, then replace all highlights at once.
            // Split into leaf and non-leaf: selectObjectHierarchy batches all leaf IDs
            // in one dispatch, but only processes nodes[0] for non-leaf.
            const leafObjects: ModelTreeNodeData["object"][] = [];
            const nonLeafObjects: ModelTreeNodeData["object"][] = [];
            for (const p of newPaths) {
                const n = tree[p];
                if (!n) {
                    continue;
                }
                if (n.isLeaf) {
                    leafObjects.push(n.data.object);
                } else {
                    nonLeafObjects.push(n.data.object);
                }
            }

            if (leafObjects.length > 0 || nonLeafObjects.length > 0) {
                const afterHighlightedParts: number[][] = [];
                try {
                    if (leafObjects.length > 0) {
                        const leafIds = await selectObjectHierarchy({
                            nodes: leafObjects,
                            dataAccess,
                            abortSignal: signal,
                            setLoading,
                            dispatchHighlighted: () => {},
                        });
                        afterHighlightedParts.push(leafIds);
                    }
                    if (nonLeafObjects.length > 0 && !signal.aborted) {
                        setLoading(true);
                        try {
                            const nonLeafIds = await bulkSelectAllHierarchies({
                                nodes: nonLeafObjects,
                                dataAccess,
                                abortSignal: signal,
                                dispatchHighlighted,
                            });
                            afterHighlightedParts.push(nonLeafIds);
                        } finally {
                            setLoading(false);
                        }
                    }
                } catch (error) {
                    if (!signal.aborted) {
                        telemetry.trackCaughtException({
                            source: "objectTreeWindowV2.handleRowSelect",
                            category: "data",
                            error,
                            severity: "error",
                        });
                    }
                }
                if (signal.aborted || selectGenerationRef.current !== generation) {
                    return;
                }
                dispatchHighlighted(highlightActions.setIds(afterHighlightedParts.flat()));
                treeInitiatedRef.current = true;
                dispatch(renderActions.setMainObject(node.data.object.id));
                const newMainObjects = [...newPaths]
                    .map((p) => tree[p]?.data.object.id)
                    .filter((id): id is number => id !== undefined && id !== SYNTHETIC_NODE_ID);
                dispatch(renderActions.setMainObjects(newMainObjects));
                recordSelection({
                    highlightedIds: afterHighlightedParts.flat(),
                    mainObject: node.data.object.id,
                    mainObjects: newMainObjects,
                });
            } else {
                dispatchHighlighted(highlightActions.setIds([]));
                dispatch(renderActions.setMainObject(undefined));
                dispatch(renderActions.clearMainObjects());
                if (!isSelectionEmpty(before)) {
                    recordSelection({ highlightedIds: [], mainObject: undefined, mainObjects: [] });
                }
            }
        },
        [
            tree,
            list,
            selectedRowPaths,
            dataAccess,
            dispatch,
            dispatchHighlighted,
            selectAbortController,
            abortSelection,
            isIsolatedInXRay,
            mainObject,
            mainObjects,
            captureSelection,
        ],
    );

    // Visibility button clicked on a selected row — act on all selected rows.
    // Hiding leaves the selection alone.
    const handleToggleVisibilityForSelection = useCallback(
        async (hoveredIsHidden: boolean) => {
            const objects = [...selectedRowPaths]
                .map((p) => tree[p]?.data.object)
                .filter((o): o is ModelTreeNodeData["object"] => o != null);
            if (isolation.active) {
                for (const obj of objects) {
                    if (abortController.current.signal.aborted) {
                        break;
                    }
                    await isolation.setHierarchyVisible({
                        nodes: [obj],
                        visible: hoveredIsHidden,
                        dataAccess,
                        abortSignal: abortController.current.signal,
                        setLoading,
                        filteredObjectIds,
                    });
                }
                return;
            }
            const hiddenBefore = hiddenIdsRef.current;
            const affectedParts: number[][] = [];
            if (hoveredIsHidden) {
                for (const obj of objects) {
                    if (abortController.current.signal.aborted) {
                        break;
                    }
                    const params = {
                        nodes: [obj],
                        dataAccess,
                        abortSignal: abortController.current.signal,
                        setLoading,
                        dispatchHidden,
                    };
                    if (filteredObjectIds != null) {
                        const shown = await showObjectHierarchyFromFilteredTree({ ...params, filteredObjectIds });
                        affectedParts.push(shown);
                    } else {
                        const shown = await showObjectHierarchy(params);
                        affectedParts.push(shown);
                    }
                }
            } else {
                for (const obj of objects) {
                    if (abortController.current.signal.aborted) {
                        break;
                    }
                    const params = {
                        nodes: [obj],
                        dataAccess,
                        abortSignal: abortController.current.signal,
                        setLoading,
                        dispatchHidden,
                    };
                    if (filteredObjectIds != null) {
                        const hidden = await hideObjectHierarchyFromFilteredTree({ ...params, filteredObjectIds });
                        affectedParts.push(hidden);
                    } else {
                        const hidden = await hideObjectHierarchy(params);
                        affectedParts.push(hidden);
                    }
                }
            }
            pushVisibilityUndo({
                dispatch,
                dispatchHidden,
                affectedIds: affectedParts.flat(),
                hiddenBefore,
                shown: hoveredIsHidden,
            });
        },
        [selectedRowPaths, tree, dataAccess, abortController, dispatch, dispatchHidden, isolation, filteredObjectIds],
    );

    const handleSelectAll = useCallback(async () => {
        if (isIsolatedInXRay || list.length === 0) {
            return;
        }
        const generation = ++selectGenerationRef.current;
        // See handleRowSelect: abort the previous run and capture this run's signal once.
        abortSelection();
        const signal = selectAbortController.current.signal;
        pendingViewerSelectRef.current = null;
        isViewerPickRef.current = false;
        viewerPickMainObjectRef.current = null;

        const newPaths = new Set(list.map((i) => i.node.path));
        anchorRowPathRef.current = list[0].node.path;
        setSelectedRowPaths(newPaths);

        // During search the list also contains ancestor rows that are only there to
        // show the path. Hierarchy-selecting those pulls in the rest of the model.
        // Only the rows whose ids are search matches get their geometry selected.
        const leafObjects: ModelTreeNodeData["object"][] = [];
        const nonLeafObjects: ModelTreeNodeData["object"][] = [];
        for (const { node } of list) {
            const n = tree[node.path];
            if (!n) {
                continue;
            }
            if (filteredObjectIds != null && !filteredObjectIds.has(n.data.object.id)) {
                continue;
            }
            if (n.isLeaf) {
                leafObjects.push(n.data.object);
            } else {
                nonLeafObjects.push(n.data.object);
            }
        }

        if (leafObjects.length === 0 && nonLeafObjects.length === 0) {
            return;
        }

        dispatchHighlighted(highlightActions.setIds([]));
        try {
            if (leafObjects.length > 0) {
                await selectObjectHierarchy({
                    nodes: leafObjects,
                    dataAccess,
                    abortSignal: signal,
                    setLoading,
                    dispatchHighlighted,
                });
            }
            if (nonLeafObjects.length > 0 && !signal.aborted) {
                setLoading(true);
                try {
                    await bulkSelectAllHierarchies({
                        nodes: nonLeafObjects,
                        dataAccess,
                        abortSignal: signal,
                        dispatchHighlighted,
                    });
                } finally {
                    setLoading(false);
                }
            }
        } catch (error) {
            if (!signal.aborted) {
                telemetry.trackCaughtException({
                    source: "objectTreeWindowV2.handleSelectAll",
                    category: "data",
                    error,
                    severity: "error",
                });
            }
        }
        if (signal.aborted || selectGenerationRef.current !== generation) {
            return;
        }
        const firstNode = list[0].node;
        treeInitiatedRef.current = true;
        dispatch(renderActions.setMainObject(firstNode.data.object.id));
        const newMainObjects = list
            .map((i) => i.node.data.object.id)
            .filter((id): id is number => id !== undefined && id !== SYNTHETIC_NODE_ID);
        dispatch(renderActions.setMainObjects(newMainObjects));
    }, [
        list,
        tree,
        isIsolatedInXRay,
        dataAccess,
        dispatch,
        dispatchHighlighted,
        selectAbortController,
        abortSelection,
        filteredObjectIds,
    ]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.code === "KeyA") {
                e.preventDefault();
                void handleSelectAll();
            }
        },
        [handleSelectAll],
    );

    // Right-click: if the row is not in the selection, collapse to just that row first.
    const handleNodeRightClick = useCallback(
        async (_x: number, _y: number, item: ListNode<ModelTreeNodeData>) => {
            const path = item.node.path;
            if (!selectedRowPaths.has(path)) {
                const generation = ++selectGenerationRef.current;
                abortSelection();
                const signal = selectAbortController.current.signal;
                const before = captureSelection();
                const newPaths = new Set([path]);
                setSelectedRowPaths(newPaths);
                anchorRowPathRef.current = path;
                pendingViewerSelectRef.current = null;
                const selectedIds = await selectObjectHierarchy({
                    nodes: [item.node.data.object],
                    dataAccess,
                    abortSignal: signal,
                    setLoading,
                    dispatchHighlighted: () => {},
                });
                if (signal.aborted || selectGenerationRef.current !== generation) {
                    return;
                }
                dispatchHighlighted(highlightActions.setIds(selectedIds));
                treeInitiatedRef.current = true;
                dispatch(renderActions.setMainObject(item.node.data.object.id));
                if (item.node.data.object.id !== SYNTHETIC_NODE_ID) {
                    dispatch(renderActions.setMainObjects([item.node.data.object.id]));
                }
                pushSelectionUndo({
                    dispatch,
                    dispatchHighlighted,
                    description: "Select objects",
                    before,
                    after: {
                        highlightedIds: selectedIds,
                        mainObject: item.node.data.object.id,
                        mainObjects:
                            item.node.data.object.id !== SYNTHETIC_NODE_ID
                                ? [item.node.data.object.id]
                                : mainObjectsRef.current,
                    },
                });
            }
            setContextMenu({ x: _x, y: _y });
        },
        [
            selectedRowPaths,
            dataAccess,
            dispatch,
            dispatchHighlighted,
            selectAbortController,
            abortSelection,
            captureSelection,
        ],
    );

    const contextMenuActions = useMemo((): BulkAction[] => {
        const close = () => setContextMenu(null);
        return [
            {
                id: "hide-selection",
                label: allSelectedHidden ? t("unhideSelection") : t("hideSelection"),
                onClick: () => {
                    close();
                    void track(Events.bimObjects.hidden, { origin: EventOrigin.ObjectTree });
                    void handleToggleVisibilityForSelection(allSelectedHidden);
                },
                disabled: false,
            },
            {
                id: "zoom-to-selection",
                label: t("zoomToSelection"),
                onClick: () => {
                    void flyToSelected();
                    void track(Events.bimObjects.zoomedToSelection, { origin: EventOrigin.ObjectTree });
                    close();
                },
                disabled: false,
            },
            {
                id: "isolate",
                label: isIsolated ? t("unisolate") : t("isolate"),
                onClick: () => {
                    if (isIsolated) {
                        unisolateObjects({
                            dispatch,
                            isolationIds: isolation.keepIds?.sortedIds ?? [],
                            restoreToVisibility: preIsolationVisibility ?? ObjectVisibility.Neutral,
                        });
                        void track(Events.bimObjects.unisolated, { origin: EventOrigin.ObjectTree });
                    } else {
                        isolateObjects({
                            dispatch,
                            isolationIds: highlightedIds.sortedIds,
                            currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
                            visibilityBefore: captureVisibilitySnapshot(store.getState()),
                            propertyFilter: capturePropertyFilter(store.getState()),
                        });
                        void track(Events.bimObjects.isolated, { origin: EventOrigin.ObjectTree });
                    }
                    close();
                },
                disabled: false,
            },
            {
                id: "isolate-xray",
                label: isIsolatedInXRay ? t("unisolateFromXRay") : t("isolateInXRay"),
                onClick: () => {
                    if (isIsolatedInXRay) {
                        unisolateObjectsFromXRayMode({
                            dispatch,
                            restoreToVisibility: preIsolationVisibility ?? ObjectVisibility.Neutral,
                            propertyFilter: capturePropertyFilter(store.getState()),
                        });
                        void track(Events.bimObjects.unisolatedFromXray, { origin: EventOrigin.ObjectTree });
                    } else {
                        isolateObjectsInXRayMode({
                            dispatch,
                            currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
                            propertyFilter: capturePropertyFilter(store.getState()),
                        });
                        void track(Events.bimObjects.isolatedInXray, { origin: EventOrigin.ObjectTree });
                    }
                    close();
                },
                disabled: false,
            },
            {
                id: "select-all",
                label: t("selectAll"),
                dividerAbove: true,
                onClick: () => {
                    close();
                    void handleSelectAll();
                },
                disabled: isIsolatedInXRay || list.length === 0,
            },
            {
                id: "deselect-selection",
                label: t("deselectSelection"),
                dividerAbove: false,
                onClick: () => {
                    abortSelection();
                    const before = captureSelection();
                    treeInitiatedRef.current = true;
                    setSelectedRowPaths(new Set());
                    anchorRowPathRef.current = null;
                    dispatchHighlighted(highlightActions.setIds([]));
                    dispatch(renderActions.setMainObject(undefined));
                    dispatch(renderActions.clearMainObjects());
                    if (!isSelectionEmpty(before)) {
                        pushSelectionUndo({
                            dispatch,
                            dispatchHighlighted,
                            description: "Unselect objects",
                            before,
                            after: { highlightedIds: [], mainObject: undefined, mainObjects: [] },
                        });
                    }
                    close();
                },
                disabled: false,
            },
        ];
    }, [
        allSelectedHidden,
        isIsolated,
        isIsolatedInXRay,
        t,
        handleSelectAll,
        handleToggleVisibilityForSelection,
        captureSelection,
        flyToSelected,
        dispatch,
        dispatchHighlighted,
        store,
        isolation,
        highlightedIds,
        preIsolationVisibility,
        defaultVisibility,
        list,
        abortSelection,
    ]);

    const selectionContext = useMemo(
        () => ({
            selectedRowPaths,
            list,
            onRowSelect: handleRowSelect,
            onToggleVisibilityForSelection: handleToggleVisibilityForSelection,
        }),
        [selectedRowPaths, list, handleRowSelect, handleToggleVisibilityForSelection],
    );

    // On drill-away, clear the error lock for the path we're leaving so it can
    // page again if the user returns. Cleanup runs after the new path's child
    // effects, so it never races the new path's handleLoadMore.
    useEffect(() => {
        const path = currentPath;
        return () => {
            if (mobileLoadingPath.current === path) {
                mobileLoadingPath.current = null;
            }
        };
    }, [currentPath]);

    // Drill into a group tapped from the search results view. clearSearchState alone would
    // restore the (lazy) unfiltered tree that likely does not contain the tapped path — the
    // subsequent expandNode short-circuits, leaving MobileObjectTreeLevel on a spinner.
    // Instead: seed the ancestor chain via selectNode against the base tree first, then push
    // every ancestor onto the stack so Back walks up one folder at a time (setStack([path])
    // alone would jump straight to root).
    const handleSelectSearchGroup = useCallback(
        async (path: string) => {
            const generation = ++selectSearchGroupGenerationRef.current;
            const restoreUnfilteredRoot = () => {
                if (selectSearchGroupGenerationRef.current !== generation) {
                    return;
                }
                setTree(getBaseTree());
                setStack([]);
            };

            const searchNode = treeRef.current[path];
            if (!searchNode) {
                handleSearchChange("");
                setStack([]);
                return;
            }
            const mainObject = searchNode.data.object;
            const ancestorStack = [...getParentPaths(path, false), path];

            abortDrill();
            const signal = drillAbortController.current.signal;

            const baseTree = getBaseTree();
            const loader = selectNode({
                tree: baseTree,
                dataAccess,
                mainObject,
                abortSignal: signal,
                skipPdfFiles,
            });

            // handleSearchChange("") clears the input (clearSearchState does not) and the
            // filter flags. Its setTree(baseTree) runs first; ours overwrites with the
            // loading skeleton in the same tick so the intermediate empty state never renders.
            handleSearchChange("");
            setTree(loader.tree);

            if (!loader.load) {
                setStack(ancestorStack);
                return;
            }

            setLoading(true);
            try {
                const result = await loader.load();
                if (selectSearchGroupGenerationRef.current !== generation || signal.aborted) {
                    restoreUnfilteredRoot();
                    return;
                }
                setTree((t) => {
                    let next = result.apply(t, true);
                    // selectNode marks the target node expanded but never fetched its own
                    // children (it only loaded direct children of each ancestor). Un-mark so
                    // the drill effect's expandNode call actually loads them — expandNode
                    // returns early when expanded is already true.
                    const target = next[path];
                    if (target && !target.children && target.expanded) {
                        next = { ...next, [path]: { ...target, expanded: false } };
                    }
                    return next;
                });
                setStack(ancestorStack);
            } catch (ex) {
                restoreUnfilteredRoot();
                if ((ex as { name?: string }).name !== "AbortError") {
                    telemetry.trackCaughtException({
                        source: "objectTreeWindowV2.handleSelectSearchGroup",
                        category: "data",
                        error: ex,
                        severity: "error",
                    });
                }
            } finally {
                if (selectSearchGroupGenerationRef.current === generation) {
                    setLoading(false);
                }
            }
        },
        [abortDrill, drillAbortController, getBaseTree, dataAccess, skipPdfFiles, handleSearchChange, setTree],
    );

    const handleLoadMore = useCallback(async () => {
        if (mobileLoadingPath.current === currentPath) {
            return;
        }
        const node = treeRef.current[currentPath];
        if (!node?.hasMoreChildren || node.loading) {
            return;
        }
        // Capture path so the finally guard matches even if currentPath changes mid-flight.
        const path = currentPath;
        mobileLoadingPath.current = path;
        const signal = loadMoreAbortController.current.signal;
        const loader = loadMore({
            dataAccess,
            tree: treeRef.current,
            path,
            abortSignal: signal,
            skipPdfFiles,
            knownPdfObjectIds,
        });
        setTree(loader.tree);
        try {
            if (loader.load) {
                const patch = await loader.load();
                setTree((t) => patch(t, true));
            }
        } catch (ex: unknown) {
            if (loader.rollback) {
                setTree((t) => loader.rollback!(t));
            }
            if ((ex as { name?: string }).name !== "AbortError") {
                telemetry.trackCaughtException({
                    source: "objectTreeWindowV2.loadMore",
                    category: "data",
                    error: ex,
                    severity: "error",
                });
            }
        } finally {
            // Always release the lock so the observer can retry on the next
            // intersection — matching V1's autoLoadControllersRef.delete(path) in finally.
            // The isPaging guard in MobileObjectTreeLevel prevents a storm while
            // the fetch is in flight; once it completes (success or error), the
            // next scroll-into-view triggers a fresh attempt.
            if (mobileLoadingPath.current === path) {
                mobileLoadingPath.current = null;
            }
        }
    }, [currentPath, dataAccess, skipPdfFiles, knownPdfObjectIds, loadMoreAbortController, setTree]);

    const currentNode = tree[currentPath];

    const truncatedSearchNotice =
        resultsTruncated && !isSearching ? (
            <div
                data-testid="search-results-truncated"
                style={{
                    flexShrink: 0,
                    padding: "8px 16px",
                    textAlign: "center",
                    color: "#6B7280",
                    fontSize: 14,
                }}
            >
                {t("searchResultsTruncated", { count: MAX_SEARCH_RESULTS })}
            </div>
        ) : null;

    return (
        <SlottedWindow
            position={position}
            size={{ width: 400, height: 600 }}
            onClose={onClose}
            loading={isSearching || (isMobile && loading)}
            emptyState={
                // Same gate as the V1 window: the panel is answered entirely through `dataAccess`.
                // Desktop search with no matches still uses the window empty state (#1740).
                dbStatus.isAvailable
                    ? !isMobile && hasNoResults
                        ? { title: t("noResultsFound"), message: t("noResultsFoundHint") }
                        : undefined
                    : dbStatus.unavailableReason === "offline"
                      ? { title: t("noInternetConnection"), message: t("objectTreeOffline"), showIcon: false }
                      : { title: t("databasePreparing"), message: t("objectTreePreparing"), showIcon: false }
            }
            headerRegion={
                // During search (results/empty/error/in-flight) the body renders search
                // results, not the drill stack, so Back would be a visual no-op — show the
                // root title until the user leaves the search state instead.
                isMobile && !isFilterActive && stack.length > 0 ? (
                    <SlottedWindow.HeaderRow>
                        <SlottedWindow.BackButton onClick={() => setStack((prev) => prev.slice(0, -1))} />
                        <SlottedWindow.Title>
                            {currentNode ? formatNodeDisplayName({ node: currentNode, parent: null, level: 0 }) : ""}
                        </SlottedWindow.Title>
                    </SlottedWindow.HeaderRow>
                ) : (
                    <SlottedWindow.Title>{t("objectTree")}</SlottedWindow.Title>
                )
            }
            subheaderRegion={
                <SlottedWindow.SearchInput
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder={t("searchPlaceholder")}
                />
            }
        >
            {isMobile ? (
                searchError ? (
                    <EmptyState title={t("objectTreeSearchError")} message={t("errorLoadingSearchResults")} />
                ) : hasNoResults ? (
                    <EmptyState
                        title={t("noResultsFound")}
                        message={t("noResultsFoundHint")}
                        illustration={
                            <div
                                style={{
                                    width: 171,
                                    height: 171,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <NoSearchResultsMobileIcon
                                    data-testid="no-search-results-illustration"
                                    aria-hidden="true"
                                    style={{ width: 154.5, height: "auto" }}
                                />
                            </div>
                        }
                    />
                ) : isFilterActive && filteredObjectIds ? (
                    <div style={{ display: "flex", height: "100%", minHeight: 0, flexDirection: "column" }}>
                        {truncatedSearchNotice}
                        <div style={{ flex: 1, minHeight: 0 }}>
                            <MobileSearchResults
                                tree={tree}
                                filteredObjectIds={filteredObjectIds}
                                searchTerm={searchTerm}
                                onSelectGroup={(path) => {
                                    void handleSelectSearchGroup(path);
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <MobileObjectTreeLevel
                        key={currentPath}
                        tree={tree}
                        drillPath={currentPath}
                        onDrillIn={(path) => setStack((prev) => [...prev, path])}
                        onLeafTap={() => {
                            /* BM-13042 */
                        }}
                        onLoadMore={handleLoadMore}
                    />
                )
            ) : (
                /* Clicking empty space below rows deselects all (req 21) */
                <div
                    style={{
                        height: "100%",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {truncatedSearchNotice}
                    <div
                        style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
                        tabIndex={-1}
                        onKeyDown={handleKeyDown}
                        onClick={() => {
                            abortSelection();
                            const before = captureSelection();
                            treeInitiatedRef.current = true;
                            setSelectedRowPaths(new Set());
                            anchorRowPathRef.current = null;
                            dispatchHighlighted(highlightActions.setIds([]));
                            dispatch(renderActions.setMainObject(undefined));
                            dispatch(renderActions.clearMainObjects());
                            // Clicking empty space with nothing selected is a no-op.
                            if (!isSelectionEmpty(before)) {
                                pushSelectionUndo({
                                    dispatch,
                                    dispatchHighlighted,
                                    description: "Unselect objects",
                                    before,
                                    after: { highlightedIds: [], mainObject: undefined, mainObjects: [] },
                                });
                            }
                        }}
                    >
                        <SelectionContext.Provider value={selectionContext}>
                            <ModelTree
                                key={treeKey}
                                tree={tree}
                                setTree={setTree}
                                fileTypes={emptyFileTypeFilters}
                                NodeRenderer={ObjectTreeNodeProcoreV2}
                                itemSize={ITEM_HEIGHT_V2}
                                filteredObjectIds={filteredObjectIds}
                                isFilterActive={isFilterActive}
                                searchTerm={searchTerm}
                                headerMode="sticky"
                                scrollAlignment="center"
                                noHorizontalScroll
                                onNodeRightClick={handleNodeRightClick}
                                // Must match the selection list above so visible rows and
                                // shift-click ranges contain the same ungrouped paths.
                                grouping={false}
                            />
                        </SelectionContext.Provider>
                    </div>
                </div>
            )}

            <ContextMenuPopup
                contextMenu={
                    contextMenu ? { mouseX: contextMenu.x, mouseY: contextMenu.y, actions: contextMenuActions } : null
                }
                onClose={() => setContextMenu(null)}
            />
        </SlottedWindow>
    );
}
